import { Trash2, GripVertical, Percent, Pencil, Sparkles, Scale } from "lucide-react";
import { computeIngredientTotals, computeRecipeTotals, computePer100g, titleCase } from "../lib/nutrition";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const IngredientsTable = ({
  ingredients,
  onChangeQty,
  onRemove,
  onToggleQuid,
  onEdit,
  inputMode = "g",
  onInputModeChange,
  targetTotal = 0,
  onTargetTotalChange,
}) => {
  const totals = computeRecipeTotals(ingredients);
  const per100 = computePer100g(totals);
  const isPctMode = inputMode === "pct";

  // Sum of % shown to the user — based on current grams and target total
  const targetForPct = isPctMode ? (targetTotal || totals.weight_g || 0) : totals.weight_g;
  const pctSum = targetForPct > 0
    ? +ingredients.reduce((s, i) => s + ((i.quantity_g || 0) / targetForPct) * 100, 0).toFixed(1)
    : 0;

  if (!ingredients.length) {
    return (
      <div
        className="border-2 border-dashed border-stone-200 rounded-2xl p-12 text-center bg-stone-50/60"
        data-testid="empty-ingredients"
      >
        <p className="text-stone-500 text-sm">
          Aucun ingrédient. Utilisez la recherche ci-dessus pour ajouter des aliments de la base USDA.
        </p>
      </div>
    );
  }

  const handleChange = (ing, raw) => {
    const value = parseFloat(raw) || 0;
    if (isPctMode) {
      const base = targetTotal || totals.weight_g || 0;
      const grams = base > 0 ? (value / 100) * base : 0;
      onChangeQty(ing.id, +grams.toFixed(2));
    } else {
      onChangeQty(ing.id, value);
    }
  };

  return (
    <div className="space-y-3">
      {/* Mode toolbar */}
      <div
        className="flex items-center justify-between gap-3 flex-wrap p-3 bg-stone-50 border border-stone-200 rounded-xl"
        data-testid="quantity-mode-toolbar"
      >
        <div className="inline-flex items-center bg-white border border-stone-200 rounded-lg p-0.5">
          <button
            type="button"
            onClick={() => onInputModeChange?.("g")}
            className={`px-3 py-1.5 text-xs font-bold tracking-wider uppercase rounded-md transition-colors ${
              !isPctMode ? "bg-emerald-700 text-white" : "text-stone-600 hover:text-emerald-700"
            }`}
            data-testid="mode-g-btn"
          >
            <Scale className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
            Grammes
          </button>
          <button
            type="button"
            onClick={() => onInputModeChange?.("pct")}
            className={`px-3 py-1.5 text-xs font-bold tracking-wider uppercase rounded-md transition-colors ${
              isPctMode ? "bg-emerald-700 text-white" : "text-stone-600 hover:text-emerald-700"
            }`}
            data-testid="mode-pct-btn"
          >
            <Percent className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
            Pourcentage
          </button>
        </div>
        {isPctMode && (
          <div className="flex items-center gap-2" data-testid="target-total-row">
            <label className="text-xs text-stone-600 font-semibold">
              Poids total cible
            </label>
            <Input
              type="number"
              min="0"
              step="1"
              value={targetTotal || ""}
              onChange={(e) => onTargetTotalChange?.(parseFloat(e.target.value) || 0)}
              className="w-24 h-8 text-right text-sm bg-white"
              data-testid="target-total-input"
            />
            <span className="text-xs text-stone-500">g</span>
            <span
              className={`ml-2 text-xs font-semibold tabular-nums ${
                Math.abs(pctSum - 100) < 0.5 ? "text-emerald-700" : "text-amber-600"
              }`}
              data-testid="pct-sum"
            >
              Σ {pctSum}%
            </span>
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white shadow-sm">
      <table className="w-full text-left border-collapse" data-testid="ingredients-table">
        <thead>
          <tr className="border-b-2 border-stone-200 text-[10px] tracking-[0.18em] uppercase text-stone-500">
            <th className="py-3 px-4 font-bold">Ingrédient</th>
            <th className="py-3 px-3 font-bold text-right w-32">
              {isPctMode ? "Part (%)" : "Quantité (g)"}
            </th>
            <th className="py-3 px-3 font-bold text-right">Kcal</th>
            <th className="py-3 px-3 font-bold text-right">Prot.</th>
            <th className="py-3 px-3 font-bold text-right">Gluc.</th>
            <th className="py-3 px-3 font-normal text-right text-stone-400 normal-case tracking-normal italic">
              dont sucres
            </th>
            <th className="py-3 px-3 font-bold text-right">Lip.</th>
            <th className="py-3 px-3 font-bold text-right">Fibres</th>
            <th className="py-3 px-3 w-20"></th>
          </tr>
        </thead>
        <tbody>
          {ingredients.map((ing) => {
            const t = computeIngredientTotals(ing);
            return (
              <tr
                key={ing.id}
                className="border-b border-stone-100 hover:bg-stone-50/60 transition-colors"
                data-testid={`ingredient-row-${ing.id}`}
              >
                <td className="py-4 px-4 align-middle">
                  <div className="flex items-start gap-2">
                    <GripVertical className="h-4 w-4 text-stone-300 mt-1 shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-stone-900 leading-tight">
                          {ing.name_fr || titleCase(ing.description)}
                        </span>
                        {ing.custom && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold tracking-wider uppercase"
                            title="Ingrédient personnalisé (hors USDA)"
                            data-testid={`custom-badge-${ing.id}`}
                          >
                            <Sparkles className="h-2.5 w-2.5" />
                            Perso
                          </span>
                        )}
                        {ing.quid && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[9px] font-bold tracking-wider uppercase"
                            title="Pourcentage forcé sur l'étiquette INCO"
                            data-testid={`quid-badge-${ing.id}`}
                          >
                            <Percent className="h-2.5 w-2.5" />
                            QUID
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {ing.name_fr && !ing.custom && ing.source !== "BLV" ? (
                          <span className="italic">{titleCase(ing.description)} · </span>
                        ) : null}
                        {ing.brand ? `${titleCase(ing.brand)} · ` : ""}
                        {ing.custom
                          ? "Saisie manuelle"
                          : ing.source === "BLV"
                          ? `🇨🇭 BLV #${ing.blv_dbid}`
                          : `🇺🇸 USDA #${ing.fdc_id}`}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-3 align-middle text-right">
                  <div className="flex flex-col items-end gap-0.5">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        step={isPctMode ? "0.1" : "1"}
                        value={
                          isPctMode
                            ? targetForPct > 0
                              ? +(((ing.quantity_g || 0) / targetForPct) * 100).toFixed(2)
                              : 0
                            : ing.quantity_g
                        }
                        onChange={(e) => handleChange(ing, e.target.value)}
                        className="w-20 text-right bg-stone-50 border border-stone-200 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none font-semibold"
                        data-testid={`qty-input-${ing.id}`}
                      />
                      <span className="text-xs text-stone-400 w-3">
                        {isPctMode ? "%" : "g"}
                      </span>
                    </div>
                    <span className="text-[10px] text-stone-400 tabular-nums">
                      {isPctMode
                        ? `${(ing.quantity_g || 0).toFixed(1)} g`
                        : targetForPct > 0
                        ? `${(((ing.quantity_g || 0) / targetForPct) * 100).toFixed(1)} %`
                        : ""}
                    </span>
                  </div>
                </td>
                <td className="py-4 px-3 align-middle text-right font-semibold text-stone-900 tabular-nums">
                  {t.calories}
                </td>
                <td className="py-4 px-3 align-middle text-right text-stone-700 tabular-nums">
                  {t.protein}
                </td>
                <td className="py-4 px-3 align-middle text-right text-stone-700 tabular-nums">
                  {t.carbs}
                </td>
                <td className="py-4 px-3 align-middle text-right text-stone-500 tabular-nums italic">
                  {t.sugars}
                </td>
                <td className="py-4 px-3 align-middle text-right text-stone-700 tabular-nums">
                  {t.fat}
                </td>
                <td className="py-4 px-3 align-middle text-right text-stone-700 tabular-nums">
                  {t.fiber}
                </td>
                <td className="py-4 px-3 align-middle text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit?.(ing)}
                      className="h-8 w-8 text-stone-300 hover:text-emerald-700 hover:bg-emerald-50"
                      data-testid={`edit-ingredient-${ing.id}`}
                      title={ing.custom ? "Modifier l'ingrédient" : "Renommer en français"}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onToggleQuid?.(ing.id)}
                      className={`h-8 w-8 ${
                        ing.quid
                          ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                          : "text-stone-300 hover:text-emerald-700 hover:bg-emerald-50"
                      }`}
                      data-testid={`quid-toggle-${ing.id}`}
                      title={
                        ing.quid
                          ? "QUID activé : % affiché sur l'étiquette INCO"
                          : "Activer QUID (forcer l'affichage du %)"
                      }
                    >
                      <Percent className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onRemove(ing.id)}
                      className="h-8 w-8 text-stone-400 hover:text-red-600 hover:bg-red-50"
                      data-testid={`remove-ingredient-${ing.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
          <tr className="bg-stone-50 font-bold" data-testid="totals-row">
            <td className="py-4 px-4 text-stone-900">
              Total <span className="text-stone-500 font-normal text-sm ml-1">({totals.weight_g} g)</span>
            </td>
            <td className="py-4 px-3 text-right text-stone-500 text-sm">—</td>
            <td className="py-4 px-3 text-right text-emerald-700 tabular-nums" data-testid="total-calories">
              {totals.calories}
            </td>
            <td className="py-4 px-3 text-right text-stone-900 tabular-nums">{totals.protein}</td>
            <td className="py-4 px-3 text-right text-stone-900 tabular-nums">{totals.carbs}</td>
            <td className="py-4 px-3 text-right text-stone-500 tabular-nums italic" data-testid="total-sugars">
              {totals.sugars}
            </td>
            <td className="py-4 px-3 text-right text-stone-900 tabular-nums">{totals.fat}</td>
            <td className="py-4 px-3 text-right text-stone-900 tabular-nums">{totals.fiber}</td>
            <td></td>
          </tr>
          <tr className="bg-emerald-50/60 text-emerald-900" data-testid="per100-row">
            <td className="py-3 px-4 font-semibold">
              Pour 100 g
              <span className="ml-1 text-[10px] tracking-widest uppercase text-emerald-700/70 font-bold">
                INCO
              </span>
            </td>
            <td className="py-3 px-3 text-right text-emerald-700/70 text-sm">—</td>
            <td className="py-3 px-3 text-right tabular-nums font-semibold" data-testid="per100-calories">
              {per100.calories}
            </td>
            <td className="py-3 px-3 text-right tabular-nums">{per100.protein}</td>
            <td className="py-3 px-3 text-right tabular-nums">{per100.carbs}</td>
            <td className="py-3 px-3 text-right tabular-nums italic text-emerald-800/70" data-testid="per100-sugars">
              {per100.sugars}
            </td>
            <td className="py-3 px-3 text-right tabular-nums">{per100.fat}</td>
            <td className="py-3 px-3 text-right tabular-nums">{per100.fiber}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
    </div>
  );
};

export default IngredientsTable;
